import { type LoyaltyUserRegistrationInfo } from '@koala/sdk';
import Router from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SignUpForm, type SignUpFormFieldValues } from './form';
import { CAPTCHA_CONFIG, CAPTCHA_FAILURE_COPY } from '@/constants/authentication';
import { useSelector } from '@/redux';
import authActions from '@/redux/auth/actions';
import { getExtraSignupPayload } from '@/utils/auth';
import { parseReferrer, tryCatch } from '@/utils/global';
import { sanitizePhoneNumber } from '@/utils/phoneNumber';
import useCaptcha from '@/utils/useCaptcha';
import globalActions from '@/redux/global/actions';

export function SignUpFormContainer() {
  const dispatch = useDispatch();
  const {
    fetchCaptchaToken: fetchSignUpCaptchaToken,
    captchaEnabled: signUpCaptchaEnabled,
    loadingCaptcha: loadingSignUpCaptcha,
  } = useCaptcha({
    action: CAPTCHA_CONFIG.API_ACTIONS.REGISTER,
  });
  const { fetchCaptchaToken: fetchLoginCaptchaToken, captchaEnabled: loginCaptchaEnabled } =
    useCaptcha({
      action: CAPTCHA_CONFIG.API_ACTIONS.LOGIN,
    });
  const loading = useSelector((state) => state.app.auth.loading);
  const signUpConnectionError = useSelector((state) => state.app.auth.signUpConnectionError);
  const duplicateAccountError = useSelector((state) => state.app.auth.duplicateAccountError);
  const signUpError = useSelector((state) => state.app.auth.error);
  const loyaltyDriverID = useSelector(
    (state) => state.app.organization.organization.loyalty_driver_id,
  );

  /**
   * Locks the submit button for the whole submit flow — captcha fetch, register, and the
   * chained login — so it cannot be double-submitted. The Redux `loading` flag alone is
   * insufficient: at checkout `checkoutAsGuest(null)` and `AUTH_SUCCESS` clear it while the
   * signed-in credential is already written but the form is still mounted, which is the
   * reported /checkout double-submit window (TEAM-13451).
   */
  const [submitting, setSubmitting] = useState(false);

  /**
   * Re-enable the button once the flow reaches a terminal failure so the diner can retry.
   * No success reset is needed: the chained login always navigates away before `fetchMe`
   * runs (checkout → /account, /sign-up → referrer), so the form unmounts on success.
   */
  useEffect(() => {
    if (signUpConnectionError || duplicateAccountError || signUpError) {
      setSubmitting(false);
    }
  }, [signUpConnectionError, duplicateAccountError, signUpError]);

  const signUpFactory = (
    formValues: SignUpFormFieldValues,
    captchaResponse?: string | null,
    loginCaptchaResponse?: string | null,
  ) => {
    const payload: LoyaltyUserRegistrationInfo = {
      ...formValues,
      ...getExtraSignupPayload(loyaltyDriverID),
      captcha_response: captchaResponse ?? undefined,
      login_captcha_response: loginCaptchaResponse ?? undefined,
    };

    dispatch(authActions.signUp(payload, parseReferrer(Router), Router.pathname));
  };

  const submitSignUp = async (formValues: SignUpFormFieldValues) => {
    // Lock immediately — before the captcha await — so a second press during the token
    // fetch or the in-flight register cannot dispatch another signUp (TEAM-13451).
    setSubmitting(true);
    const normalizedFormValues = Object.assign({}, formValues, {
      // Formik's onSubmit receives the raw input, not yup's `.trim()` cast, so trim here — the
      // register call goes to `required|email` and an untrimmed address surfaces as a server
      // error instead of the inline message (TEAM-14991).
      email: formValues.email.trim(),
      phone: sanitizePhoneNumber(formValues.phone),
    });
    // TEAM-14519: each token is fetched only when its own action requires it, and the token
    // for the chained login only once the register token is in hand.
    const [signUpCaptchaResponse, signUpCaptchaError] = signUpCaptchaEnabled
      ? await tryCatch(fetchSignUpCaptchaToken())
      : [null, null];
    const [loginCaptchaResponse, loginCaptchaError] =
      !signUpCaptchaError && loginCaptchaEnabled
        ? await tryCatch(fetchLoginCaptchaToken())
        : [null, null];

    if (signUpCaptchaError || loginCaptchaError) {
      // Missing either required token aborts the whole sign-up (TEAM-14519); the helper has
      // already reported it. Captcha fetch failed before anything was dispatched — release
      // the lock so the diner can retry (no Redux failure flag fires on this path).
      dispatch(globalActions.displayErrorToast(CAPTCHA_FAILURE_COPY, true, true));
      setSubmitting(false);
      return;
    }
    signUpFactory(normalizedFormValues, signUpCaptchaResponse, loginCaptchaResponse);
    dispatch(authActions.checkoutAsGuest(null));
  };

  return (
    <SignUpForm
      loading={loading}
      loadingCaptcha={loadingSignUpCaptcha}
      connectionError={signUpConnectionError}
      submitting={submitting}
      doSignUp={(formValues) => {
        void submitSignUp(formValues);
      }}
    />
  );
}
